import React, { useState, useContext } from 'react';
import { Alert, Button, Card, Divider, Form, Input, Result, Space, Steps, Tag, Typography } from 'antd';
import {
    CheckCircleOutlined,
    CloseOutlined,
    LoadingOutlined,
    PhoneOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    SettingOutlined,
    SmileOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { getOrganizationsApi } from '../../api/apiInstances';
import { DentobookApplicationContext } from '../../context/DentobookApplicationContext';
import { AlertContext } from '../../context/AlertContext';
import { ALERT_DURATIONS } from '../../constants/alerts';
import logger from '../../utils/logger';

const { Title, Text, Paragraph } = Typography;

const TWILIO_CONSOLE_URL = 'https://www.twilio.com/console';
const TWILIO_SIGNUP_URL = 'https://www.twilio.com/try-twilio';
const TWILIO_SMS_DOCS_URL = 'https://www.twilio.com/docs/phone-numbers';
const TWILIO_WHATSAPP_DOCS_URL = 'https://www.twilio.com/docs/whatsapp';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

interface MessagingSetupWizardProps {
    onComplete: () => void;
    onSkip: () => void;
    onClose: () => void;
}

type VerifyState = 'idle' | 'verifying' | 'verified' | 'failed';
type TestState = 'idle' | 'sending' | 'sent' | 'failed';

const MessagingSetupWizard: React.FC<MessagingSetupWizardProps> = ({ onComplete, onSkip, onClose }) => {
    const intl = useIntl();
    const { userOrganization } = useContext(DentobookApplicationContext);
    const { setSuccessMessage, setVisible } = useContext(AlertContext);
    const organizationsApi = getOrganizationsApi();

    const [currentStep, setCurrentStep] = useState(0);

    // Step 3 — credentials
    const [accountSid, setAccountSid] = useState('');
    const [authToken, setAuthToken] = useState('');
    const [verifyState, setVerifyState] = useState<VerifyState>('idle');
    const [verifyError, setVerifyError] = useState('');

    // Step 4 — senders
    const [smsFrom, setSmsFrom] = useState('');
    const [whatsappFrom, setWhatsappFrom] = useState('');

    // Step 5 — test
    const [testPhone, setTestPhone] = useState('');
    const [testChannel, setTestChannel] = useState<'sms' | 'whatsapp'>('sms');
    const [testState, setTestState] = useState<TestState>('idle');
    const [testError, setTestError] = useState('');

    // Step 6 — enable
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);

    const orgId = userOrganization?.id;

    const handleSaveCredentials = async () => {
        if (!orgId || !accountSid || !authToken) return;
        setVerifyState('verifying');
        setVerifyError('');
        try {
            // Save credentials first
            await organizationsApi.updateOrganizationMessagingSettings(orgId, {
                twilioAccountSid: accountSid,
                twilioAuthToken: authToken,
            } as any);

            // Then verify
            const resp = await organizationsApi.verifyTwilioCredentials(orgId);
            const data = resp.data as any;
            if (data.verified) {
                setVerifyState('verified');
            } else {
                setVerifyState('failed');
                setVerifyError(data.error || intl.formatMessage({ id: 'twilio_verify_failed' }));
            }
        } catch (error: any) {
            logger.error('Credential save/verify failed:', error);
            setVerifyState('failed');
            setVerifyError(error.response?.data?.detail || intl.formatMessage({ id: 'twilio_verify_failed' }));
        }
    };

    const handleSaveSenders = async () => {
        if (!orgId) return;
        try {
            await organizationsApi.updateOrganizationMessagingSettings(orgId, {
                twilioSmsFrom: smsFrom || undefined,
                twilioWhatsappFrom: whatsappFrom || undefined,
            } as any);
            setCurrentStep(4);
        } catch (error: any) {
            logger.error('Save senders failed:', error);
        }
    };

    const handleTestSend = async () => {
        if (!orgId || !testPhone) return;
        setTestState('sending');
        setTestError('');
        try {
            const resp = await organizationsApi.testSendMessage(orgId, {
                phoneNumber: testPhone,
                channel: testChannel,
            });
            const data = resp.data as any;
            if (data.success) {
                setTestState('sent');
            } else {
                setTestState('failed');
                setTestError(data.error || intl.formatMessage({ id: 'wizard_test_failed' }));
            }
        } catch (error: any) {
            logger.error('Test send failed:', error);
            setTestState('failed');
            setTestError(error.response?.data?.detail || intl.formatMessage({ id: 'wizard_test_failed' }));
        }
    };

    const handleFinish = async () => {
        if (!orgId) return;
        try {
            await organizationsApi.updateOrganizationMessagingSettings(orgId, {
                smsEnabled,
                whatsappEnabled,
                emailEnabled: true,
                defaultChannel: smsEnabled ? 'sms' : 'email',
            } as any);
            onComplete();
        } catch (error: any) {
            logger.error('Finish wizard failed:', error);
        }
    };

    const steps = [
        { title: intl.formatMessage({ id: 'wizard_step_intro' }), icon: <SmileOutlined /> },
        { title: intl.formatMessage({ id: 'wizard_step_account' }), icon: <SettingOutlined /> },
        { title: intl.formatMessage({ id: 'wizard_step_credentials' }), icon: <SafetyCertificateOutlined /> },
        { title: intl.formatMessage({ id: 'wizard_step_senders' }), icon: <PhoneOutlined /> },
        { title: intl.formatMessage({ id: 'wizard_step_test' }), icon: <SendOutlined /> },
        { title: intl.formatMessage({ id: 'wizard_step_finish' }), icon: <CheckCircleOutlined /> },
    ];

    const phoneValid = E164_REGEX.test(testPhone);

    return (
        <Card
            style={{ maxWidth: 900, margin: '0 auto' }}
            extra={
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    size="small"
                />
            }
            title={<FormattedMessage id="wizard_intro_title" />}
        >
            <Steps
                current={currentStep}
                size="small"
                style={{ marginBottom: 32 }}
                items={steps.map((s, i) => ({
                    title: s.title,
                    icon: i === currentStep ? <LoadingOutlined style={{ display: 'none' }} /> : undefined,
                }))}
            />

            {/* ── STEP 0: Intro ── */}
            {currentStep === 0 && (
                <div>
                    <Title level={4}><FormattedMessage id="wizard_intro_title" /></Title>
                    <Paragraph><FormattedMessage id="wizard_intro_p1" /></Paragraph>
                    <Paragraph><FormattedMessage id="wizard_intro_p2" /></Paragraph>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={<FormattedMessage id="wizard_intro_sms_easy" />}
                    />
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                        message={<FormattedMessage id="wizard_intro_whatsapp_note" />}
                    />
                    <Space>
                        <Button type="primary" onClick={() => setCurrentStep(1)}>
                            <FormattedMessage id="wizard_start_setup" />
                        </Button>
                        <Button onClick={onSkip}>
                            <FormattedMessage id="wizard_skip_for_now" />
                        </Button>
                    </Space>
                </div>
            )}

            {/* ── STEP 1: Create/access Twilio account ── */}
            {currentStep === 1 && (
                <div>
                    <Title level={4}><FormattedMessage id="wizard_account_title" /></Title>
                    <Paragraph><FormattedMessage id="wizard_account_p1" /></Paragraph>
                    <Paragraph><FormattedMessage id="wizard_account_p2" /></Paragraph>

                    <Space direction="vertical" style={{ marginBottom: 24 }}>
                        <a href={TWILIO_SIGNUP_URL} target="_blank" rel="noopener noreferrer">
                            <Button type="default">
                                <FormattedMessage id="wizard_create_twilio_account" />
                            </Button>
                        </a>
                        <a href={TWILIO_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
                            <Button type="link" style={{ paddingLeft: 0 }}>
                                <FormattedMessage id="wizard_open_twilio_console" />
                            </Button>
                        </a>
                    </Space>

                    <Divider style={{ margin: '16px 0' }} />
                    <Space>
                        <Button onClick={() => setCurrentStep(0)}>
                            <FormattedMessage id="wizard_back" />
                        </Button>
                        <Button type="primary" onClick={() => setCurrentStep(2)}>
                            <FormattedMessage id="wizard_have_account" />
                        </Button>
                    </Space>
                </div>
            )}

            {/* ── STEP 2: Connect credentials ── */}
            {currentStep === 2 && (
                <div>
                    <Title level={4}><FormattedMessage id="wizard_credentials_title" /></Title>
                    <Paragraph><FormattedMessage id="wizard_credentials_p1" /></Paragraph>

                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={
                            <span>
                                <FormattedMessage id="wizard_credentials_find" />{' '}
                                <a href={TWILIO_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
                                    <FormattedMessage id="twilio_link_console" />
                                </a>
                            </span>
                        }
                    />

                    <Form layout="vertical" style={{ maxWidth: 400 }}>
                        <Form.Item label={<FormattedMessage id="twilio_account_sid_label" />}>
                            <Input
                                value={accountSid}
                                onChange={e => setAccountSid(e.target.value)}
                                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            />
                        </Form.Item>
                        <Form.Item label={<FormattedMessage id="twilio_auth_token_label" />}>
                            <Input.Password
                                value={authToken}
                                onChange={e => setAuthToken(e.target.value)}
                            />
                        </Form.Item>
                    </Form>

                    {verifyState === 'verified' && (
                        <Alert
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={<FormattedMessage id="twilio_verify_success" />}
                        />
                    )}
                    {verifyState === 'failed' && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={verifyError}
                        />
                    )}

                    <Space>
                        <Button onClick={() => setCurrentStep(1)}>
                            <FormattedMessage id="wizard_back" />
                        </Button>
                        {verifyState !== 'verified' ? (
                            <Button
                                type="primary"
                                onClick={handleSaveCredentials}
                                loading={verifyState === 'verifying'}
                                disabled={!accountSid || !authToken}
                            >
                                <FormattedMessage id="wizard_save_and_verify" />
                            </Button>
                        ) : (
                            <Button type="primary" onClick={() => setCurrentStep(3)}>
                                <FormattedMessage id="wizard_continue" />
                            </Button>
                        )}
                    </Space>
                </div>
            )}

            {/* ── STEP 3: Configure senders ── */}
            {currentStep === 3 && (
                <div>
                    <Title level={4}><FormattedMessage id="wizard_senders_title" /></Title>

                    {/* SMS */}
                    <Title level={5} style={{ marginTop: 0 }}><FormattedMessage id="messaging_section_sms" /></Title>
                    <Paragraph><FormattedMessage id="wizard_sms_sender_help" /></Paragraph>
                    <Form layout="vertical" style={{ maxWidth: 400 }}>
                        <Form.Item label={<FormattedMessage id="twilio_sms_from_label" />}>
                            <Input
                                value={smsFrom}
                                onChange={e => setSmsFrom(e.target.value)}
                                placeholder="+1234567890"
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage id="twilio_from_hint" />
                            </Text>
                        </Form.Item>
                    </Form>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* WhatsApp */}
                    <Title level={5} style={{ marginTop: 0 }}><FormattedMessage id="messaging_section_whatsapp" /></Title>
                    <Paragraph><FormattedMessage id="wizard_whatsapp_sender_help" /></Paragraph>
                    <Space direction="vertical" style={{ marginBottom: 16 }}>
                        <a href={TWILIO_WHATSAPP_DOCS_URL} target="_blank" rel="noopener noreferrer">
                            <FormattedMessage id="twilio_link_whatsapp" />
                        </a>
                    </Space>
                    <Form layout="vertical" style={{ maxWidth: 400 }}>
                        <Form.Item label={<FormattedMessage id="twilio_whatsapp_from_label" />}>
                            <Input
                                value={whatsappFrom}
                                onChange={e => setWhatsappFrom(e.target.value)}
                                placeholder="+1234567890"
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage id="twilio_from_hint" />
                            </Text>
                        </Form.Item>
                    </Form>

                    <Space>
                        <Button onClick={() => setCurrentStep(2)}>
                            <FormattedMessage id="wizard_back" />
                        </Button>
                        <Button type="primary" onClick={handleSaveSenders} disabled={!smsFrom && !whatsappFrom}>
                            <FormattedMessage id="wizard_continue" />
                        </Button>
                    </Space>
                </div>
            )}

            {/* ── STEP 4: Test send ── */}
            {currentStep === 4 && (
                <div>
                    <Title level={4}><FormattedMessage id="wizard_test_title" /></Title>
                    <Paragraph><FormattedMessage id="wizard_test_p1" /></Paragraph>

                    <Form layout="vertical" style={{ maxWidth: 400 }}>
                        <Form.Item label={<FormattedMessage id="wizard_test_channel" />}>
                            <Space>
                                <Button
                                    type={testChannel === 'sms' ? 'primary' : 'default'}
                                    onClick={() => setTestChannel('sms')}
                                    disabled={!smsFrom}
                                >
                                    SMS
                                </Button>
                                <Button
                                    type={testChannel === 'whatsapp' ? 'primary' : 'default'}
                                    onClick={() => setTestChannel('whatsapp')}
                                    disabled={!whatsappFrom}
                                >
                                    WhatsApp
                                </Button>
                            </Space>
                        </Form.Item>
                        <Form.Item
                            label={<FormattedMessage id="wizard_test_phone" />}
                            validateStatus={testPhone && !phoneValid ? 'error' : ''}
                            help={testPhone && !phoneValid ? intl.formatMessage({ id: 'invalid_phone_e164' }) : ''}
                        >
                            <Input
                                value={testPhone}
                                onChange={e => { setTestPhone(e.target.value); setTestState('idle'); }}
                                placeholder="+359888123456"
                            />
                        </Form.Item>
                    </Form>

                    {testState === 'sent' && (
                        <Alert type="success" showIcon style={{ marginBottom: 16 }}
                            message={<FormattedMessage id="wizard_test_success" />} />
                    )}
                    {testState === 'failed' && (
                        <Alert type="error" showIcon style={{ marginBottom: 16 }}
                            message={testError} />
                    )}

                    <Space>
                        <Button onClick={() => setCurrentStep(3)}>
                            <FormattedMessage id="wizard_back" />
                        </Button>
                        <Button
                            onClick={handleTestSend}
                            loading={testState === 'sending'}
                            disabled={!phoneValid}
                            icon={<SendOutlined />}
                        >
                            <span><FormattedMessage id="wizard_send_test" /></span>
                        </Button>
                        <Button type="primary" onClick={() => setCurrentStep(5)}>
                            <FormattedMessage id="wizard_continue" />
                        </Button>
                    </Space>
                </div>
            )}

            {/* ── STEP 5: Enable + finish ── */}
            {currentStep === 5 && (
                <div>
                    <Result
                        status="success"
                        title={<FormattedMessage id="wizard_finish_title" />}
                        subTitle={<FormattedMessage id="wizard_finish_subtitle" />}
                    />

                    <div style={{ maxWidth: 520, margin: '0 auto' }}>
                        <Title level={5}><FormattedMessage id="wizard_enable_channels" /></Title>

                        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><FormattedMessage id="sms_enabled_label" /></span>
                            <Button
                                type={smsEnabled ? 'primary' : 'default'}
                                size="small"
                                onClick={() => setSmsEnabled(!smsEnabled)}
                                disabled={!smsFrom}
                            >
                                {smsEnabled
                                    ? intl.formatMessage({ id: 'yes_ans' })
                                    : intl.formatMessage({ id: 'no_ans' })}
                            </Button>
                        </div>

                        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><FormattedMessage id="whatsapp_enabled_label" /></span>
                            <Button
                                type={whatsappEnabled ? 'primary' : 'default'}
                                size="small"
                                onClick={() => setWhatsappEnabled(!whatsappEnabled)}
                                disabled={!whatsappFrom}
                            >
                                {whatsappEnabled
                                    ? intl.formatMessage({ id: 'yes_ans' })
                                    : intl.formatMessage({ id: 'no_ans' })}
                            </Button>
                        </div>

                        {!smsFrom && (
                            <Alert type="info" showIcon style={{ marginBottom: 8 }}
                                message={<FormattedMessage id="wizard_no_sms_sender" />} />
                        )}
                        {!whatsappFrom && (
                            <Alert type="info" showIcon style={{ marginBottom: 8 }}
                                message={<FormattedMessage id="wizard_no_whatsapp_sender" />} />
                        )}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Space>
                            <Button onClick={() => setCurrentStep(4)}>
                                <FormattedMessage id="wizard_back" />
                            </Button>
                            <Button type="primary" size="large" onClick={handleFinish}>
                                <FormattedMessage id="wizard_finish_btn" />
                            </Button>
                        </Space>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default MessagingSetupWizard;
